import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { downloadBlob } from "@/lib/download";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { RotateCcw, Trash2, Download } from "lucide-react";

const HUMAN = {
  sales_invoices: "Sales Invoice", purchase_bills: "Purchase Bill",
  vouchers: "Voucher", parties: "Party", items: "Item",
  credit_notes: "Credit Note", debit_notes: "Debit Note",
  recurring_docs: "Recurring Doc",
};

export default function TrashRestore() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => setRows((await api.get("/trash")).data), []);
  useEffect(() => { load(); }, [load]);

  const restore = async (r) => {
    setBusy(true);
    try { await api.post(`/trash/${r.collection}/${r.id}/restore`);
      toast.success("Restored"); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const exportAll = () => downloadBlob("/organizations/mine/export", `my_tenant_${Date.now()}.zip`);

  return (
    <div className="space-y-5" data-testid="trash-restore">
      <Card className="p-4 rounded-sm">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-accent">// EXPORT</div>
            <div className="font-bold text-lg mt-0.5">Download your whole tenant</div>
            <div className="text-xs text-muted-foreground mt-1">
              A ZIP containing every account, voucher, invoice, party and item — perfect for offline backup or leaving.
              Weekly automated exports are sent to Enterprise-plan tenants automatically.
            </div>
          </div>
          <Button onClick={exportAll} className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5" data-testid="self-export-btn">
            <Download className="w-4 h-4" /> Download ZIP
          </Button>
        </div>
      </Card>

      <Card className="rounded-sm">
        <div className="p-4 border-b">
          <div className="text-[10px] font-mono uppercase tracking-widest text-accent">// TRASH</div>
          <div className="font-bold text-lg mt-0.5">Recently deleted ({rows.length})</div>
          <div className="text-xs text-muted-foreground mt-1">
            Items stay here for 30 days. After that they are permanently purged.
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="text-[10px] font-mono uppercase text-muted-foreground bg-muted/30">
            <tr>
              <th className="text-left p-3">Type</th>
              <th className="text-left p-3">Ref</th>
              <th className="text-left p-3">Deleted at</th>
              <th className="text-left p-3">Deleted by</th>
              <th />
            </tr>
          </thead>
          <tbody className="divide-y">
            {rows.map((r) => (
              <tr key={`${r.collection}-${r.id}`} data-testid={`trash-row-${r.id.slice(0,8)}`}>
                <td className="p-3 text-[11px] font-mono uppercase text-muted-foreground">{HUMAN[r.collection] || r.collection}</td>
                <td className="p-3">{r.number || r.name || r.invoice_number || r.bill_number || r.voucher_number || r.id.slice(0, 8)}</td>
                <td className="p-3 text-[11px] font-mono text-muted-foreground">{r.deleted_at?.slice(0, 16).replace("T", " ")}</td>
                <td className="p-3 text-[11px] text-muted-foreground">{r.deleted_by?.email || "—"}</td>
                <td className="p-3 text-right">
                  <Button onClick={() => restore(r)} disabled={busy} size="sm" variant="outline"
                    className="rounded-sm text-xs gap-1" data-testid={`restore-${r.id.slice(0,8)}`}>
                    <RotateCcw className="w-3 h-3" /> Restore
                  </Button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={5} className="p-8 text-center text-muted-foreground italic flex items-center justify-center gap-2">
                <Trash2 className="w-4 h-4" /> Trash is empty
              </td></tr>
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
